import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import * as d3 from 'd3';
import { organizationApi } from '../../services/api';
import { useAlert } from '../../contexts/AlertContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { employeesApi } from '../../services/api';
import { getInitials } from '../common/EmployeeAvatar';
import { COLORS } from '../../constants/radar';
import { CLUSTER_SCOPE, scopeClusters } from '../../utils/clusterScope';

const ClustersTreeView = ({
  onSelectCluster,
  viewOnly,
  initialCluster,
  readOnly,
  // ADR-123 — view-filter scoping. Both default to the pre-ADR behaviour, so the
  // component renders and behaves identically for any caller that ignores them.
  // This is NOT authorization: it changes which cards are drawn, nothing else.
  myClusterIds = null,
  scope = CLUSTER_SCOPE.ALL,
  onScopeResolved,
}) => {
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [allClusters, setAllClusters] = useState(null);
  const [selectedClusterId, setSelectedClusterId] = useState(initialCluster?.id || null);
  const svgRef = useRef();
  const containerRef = useRef();
  const [avatarContainers, setAvatarContainers] = useState([]);

  useEffect(() => {
    fetchClusters();
    return () => {
      // Cleanup avatar containers on unmount
      setAvatarContainers([]);
    };
  }, []);

  const buildHierarchy = (clusters) => {
    const rootCluster = clusters.find(c => !c.parentClusterId);
    if (!rootCluster) return null;

    const buildNode = (cluster) => {
      return {
        ...cluster,
        children: (cluster.childClusters || [])
          .map(child => clusters.find(c => c.id === child.id))
          // ADR-123: defensive. A childClusters entry with no matching row used to
          // reach buildNode(undefined) and throw, blanking the step. scopeClusters
          // prunes these, but an inconsistent payload should not blank it either.
          .filter(Boolean)
          .map(c => buildNode(c))
      };
    };

    return buildNode(rootCluster);
  };

  const fetchClusters = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getClusters();
      // ADR-123: hold the raw payload. Scoping and the hierarchy are derived, so
      // toggling scope never re-fetches (ADR-123 Decision 8 / Invariant I4).
      setAllClusters(Array.isArray(data) ? data : []);
    } catch (error) {
      showAlert('Failed to fetch clusters', 'error', error);
      console.error('Error fetching clusters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ADR-123: the MINE projection is computed whenever the data or the user's seed
  // set changes, regardless of the active scope. ScheduleSession needs it even
  // while showing ALL, to decide whether "My clusters" is reachable at all.
  const mineScope = useMemo(
    () => (allClusters ? scopeClusters(allClusters, myClusterIds) : null),
    [allClusters, myClusterIds]
  );

  useEffect(() => {
    if (mineScope) onScopeResolved?.(mineScope);
  }, [mineScope, onScopeResolved]);

  const activeScope = useMemo(() => {
    if (!allClusters) return null;
    if (scope === CLUSTER_SCOPE.MINE && mineScope) return mineScope;
    return { clusters: allClusters, selectableIds: null };
  }, [allClusters, scope, mineScope]);

  // `selectableIds === null` means "no scoping applied" — every non-root node is
  // selectable, exactly as before ADR-123.
  const selectableIds = activeScope?.selectableIds ?? null;
  const isSelectable = useCallback(
    (id) => !selectableIds || selectableIds.has(id),
    [selectableIds]
  );

  const clustersData = useMemo(
    () => (activeScope ? buildHierarchy(activeScope.clusters) : null),
    // buildHierarchy is pure over its argument; it is redefined each render but
    // never closes over anything, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeScope]
  );

  useEffect(() => {
    if (!clustersData || !svgRef.current || !containerRef.current) return;

    // Clear existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();
    setAvatarContainers([]);

    const margin = { top: 40, right: 40, bottom: 40, left: 40 };
    const nodeHeight = 80;
    const nodeWidth = 250;
    const verticalSpacing = 60;

    // Create hierarchy from the root node
    const root = d3.hierarchy(clustersData);

    // Calculate tree layout
    const treeLayout = d3.tree()
      .nodeSize([nodeWidth + 40, nodeHeight + verticalSpacing]);

    const tree = treeLayout(root);

    // Get the bounds of the tree
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    tree.each(d => {
      minX = Math.min(minX, d.x);
      maxX = Math.max(maxX, d.x);
      minY = Math.min(minY, d.y);
      maxY = Math.max(maxY, d.y);
    });

    // Calculate dimensions based on the tree bounds
    const width = maxX - minX + nodeWidth + margin.left + margin.right;
    const height = maxY - minY + nodeHeight + margin.top + margin.bottom;

    // Create SVG with calculated dimensions
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    // Create main group and center it
    const g = svg.append("g")
      .attr("transform", `translate(${-minX + margin.left + nodeWidth / 2},${margin.top})`);

    // Add links
    g.selectAll(".link")
      .data(tree.links())
      .enter()
      .append("path")
      .attr("class", "link")
      .attr("fill", "none")
      .attr("stroke", "#94A3B8")
      .attr("stroke-width", 1)
      .attr("d", d => {
        return `M${d.source.x},${d.source.y}
                C${d.source.x},${(d.source.y + d.target.y) / 2}
                 ${d.target.x},${(d.source.y + d.target.y) / 2}
                 ${d.target.x},${d.target.y}`;
      });

    // Add nodes
    const node = g.selectAll(".node")
      .data(tree.descendants())
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", d => `translate(${d.x},${d.y})`)
      // ADR-123: ancestor-only nodes are drawn so the tree keeps a single connected
      // root, but they are context, not choices — de-emphasised and not clickable.
      .style("opacity", d => (d.depth !== 0 && !isSelectable(d.data.id) ? 0.45 : 1))
      .style("cursor", d => {
        if (d.depth === 0) return "default";
        if (!isSelectable(d.data.id)) return "default";
        return readOnly ? "default" : "pointer";
      })
      .on("click", (event, d) => {
        if (d.depth !== 0 && onSelectCluster && !readOnly && isSelectable(d.data.id)) {
          setSelectedClusterId(d.data.id);
          onSelectCluster(d.data);
        }
      });

    // Add node rectangles
    node.append("rect")
      .attr("width", nodeWidth)
      .attr("height", nodeHeight)
      .attr("x", -nodeWidth / 2)
      .attr("y", -nodeHeight / 2)
      .attr("rx", 8)
      .attr("ry", 8)
      .attr("fill", d => {
        if (d.depth === 0) return "#F8FAFC";
        return d.data.id === selectedClusterId ? COLORS.BACKGROUND_PRIMARY : "#FFFFFF";
      })
      .attr("stroke", d => d.data.id === selectedClusterId ? COLORS.PRIMARY : "#E2E8F0")
      .attr("stroke-width", d => d.data.id === selectedClusterId ? 1 : 1)
      .attr("stroke-dasharray", d => d.depth === 0 ? "4,4" : "none");

    // Add node content
    node.each(function(d) {
      const g = d3.select(this);
      const isRoot = d.depth === 0;

      // Add name
      g.append("text")
        .attr("x", -nodeWidth / 2 + 16)
        .attr("y", -nodeHeight / 4)
        .attr("fill", COLORS.PRIMARY)
        .attr("font-weight", "500")
        .attr("font-size", isRoot ? "14px" : "16px")
        .text(d.data.name);

      // ADR-119: the cluster's Organizational Structure Type, right-aligned as a
      // muted code with the full name as tooltip — the same presentation
      // ClusterSelector and BusinessUnitSelector use.
      if (d.data.orgStructureType?.code) {
        g.append("text")
          .attr("x", nodeWidth / 2 - 16)
          .attr("y", -nodeHeight / 4)
          .attr("text-anchor", "end")
          .attr("fill", "#6B7280")
          .attr("font-family", "monospace")
          .attr("font-size", "12px")
          .text(d.data.orgStructureType.code)
          .append("title")
          .text(d.data.orgStructureType.name);
      }

      // Add manager information for all nodes
      if (d.data.manager) {
        // Create clip path for avatar
        const clipId = `clip-${Math.random().toString(36).substr(2, 9)}`;
        
        g.append("defs")
          .append("clipPath")
          .attr("id", clipId)
          .append("circle")
          .attr("r", 12)
          .attr("cx", -nodeWidth / 2 + 28)
          .attr("cy", 0);

        // Create placeholder avatar container
        const avatarContainer = g.append("g")
          .attr("class", "manager-avatar-placeholder");
        
        // Add fallback circle as placeholder
        avatarContainer.append("circle")
          .attr("cx", -nodeWidth / 2 + 28)
          .attr("cy", 0)
          .attr("r", 12)
          .attr("fill", "#E2E8F0")
          .attr("stroke", "#94A3B8")
          .attr("stroke-width", 1);

        // Add initials to placeholder
        avatarContainer.append("text")
          .attr("x", -nodeWidth / 2 + 28)
          .attr("y", 0)
          .attr("dy", "0.35em")
          .attr("text-anchor", "middle")
          .attr("fill", "#475569")
          .attr("font-size", "12px")
          .text(getInitials(d.data.manager.fullName));
        
        // Load manager photo asynchronously without blocking
        if (d.data.manager.azureId) {
          employeesApi.getEmployeePhoto(d.data.manager.azureId)
            .then(photoUrl => {
              if (photoUrl) {
                // Remove placeholder elements
                avatarContainer.selectAll("*").remove();
                
                // Add manager photo
                avatarContainer.append("image")
                  .attr("x", -nodeWidth / 2 + 16)
                  .attr("y", -12)
                  .attr("width", 24)
                  .attr("height", 24)
                  .attr("clip-path", `url(#${clipId})`)
                  .attr("xlink:href", photoUrl)
                  .attr("preserveAspectRatio", "xMidYMid slice");
              }
            })
            .catch(error => {
              console.error('Error loading manager photo:', error);
              // Placeholder already rendered, no need to do anything
            });
        }

        // Add manager name next to avatar
        g.append("text")
          .attr("x", -nodeWidth / 2 + 48)
          .attr("y", 0)
          .attr("dy", "0.35em")
          .attr("fill", "#64748B")
          .attr("font-size", "12px")
          .text(d.data.manager.fullName);
      } else {
        // Add text for no manager
        g.append("text")
          .attr("x", -nodeWidth / 2 + 16)
          .attr("y", 0)
          .attr("dy", "0.35em")
          .attr("fill", "#94A3B8")
          .attr("font-size", "12px")
          .attr("font-style", "italic")
          .text("No manager assigned");
      }
    });

  }, [clustersData, onSelectCluster, readOnly, selectedClusterId, isSelectable]);

  if (isLoading) {
    return <LoadingSpinner message="Loading clusters..." />;
  }

  return (
    <Box ref={containerRef}>
      <Box 
        sx={{ 
          border: '1px solid #E2E8F0',
          borderRadius: 2,
          p: 3,
          backgroundColor: '#FFFFFF',
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '600px',
          '& svg': {
            display: 'block',
            margin: '0 auto',
          }
        }}
      >
        <svg 
          ref={svgRef}
          style={{
            minWidth: '800px',
            minHeight: '400px',
          }}
        />
      </Box>
      {!viewOnly && !readOnly && (
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mt: 2,
            color: '#64748B'
          }}
        >
          Click on a cluster to select it
        </Typography>
      )}
    </Box>
  );
};

export default ClustersTreeView; 